import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Trash2 } from "lucide-react";
import { CalendarEvent, CalendarEventColor, EVENT_COLORS } from "./types";

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  initial: Partial<CalendarEvent> | null;
  onSave: (event: CalendarEvent) => void;
  onDelete?: (id: string) => void;
}

function addOneHour(time: string): string {
  const [h, m] = time.split(":").map(Number);
  if (isNaN(h)) return "10:00";
  const next = (h + 1) % 24;
  return `${String(next).padStart(2, "0")}:${String(m || 0).padStart(2, "0")}`;
}

export function EventModal({ open, onOpenChange, initial, onSave, onDelete }: Props) {
  const isEdit = !!initial?.id;
  const today = new Date().toISOString().slice(0, 10);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime] = useState("10:00");
  const [color, setColor] = useState<CalendarEventColor>("blue");
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTitle(initial?.title ?? "");
    setDescription(initial?.description ?? "");
    setStartDate(initial?.startDate ?? today);
    setEndDate(initial?.endDate ?? initial?.startDate ?? today);
    setStartTime(initial?.startTime ?? "09:00");
    setEndTime(initial?.endTime ?? addOneHour(initial?.startTime ?? "09:00"));
    setColor((initial?.color as CalendarEventColor) ?? "blue");
    setConfirmDelete(false);
  }, [open, initial]);

  const handleStartTimeChange = (v: string) => {
    setStartTime(v);
    // auto-fill end time = start + 1h
    setEndTime(addOneHour(v));
  };

  const handleStartDateChange = (v: string) => {
    setStartDate(v);
    if (endDate < v) setEndDate(v);
  };

  const handleSubmit = () => {
    if (!title.trim()) return;
    const finalEnd = endDate < startDate ? startDate : endDate;
    onSave({
      id: initial?.id ?? crypto.randomUUID(),
      title: title.trim(),
      description: description.trim() || undefined,
      startDate,
      endDate: finalEnd,
      startTime,
      endTime,
      color,
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Editar evento" : "Novo evento"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="ev-title">Título</Label>
            <Input
              id="ev-title"
              autoFocus
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ex: Reunião com cliente"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="ev-desc">Descrição (opcional)</Label>
            <Textarea
              id="ev-desc"
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Notas, link da reunião..."
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Data início</Label>
              <Input type="date" value={startDate} onChange={(e) => handleStartDateChange(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Data término</Label>
              <Input type="date" value={endDate} min={startDate} onChange={(e) => setEndDate(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Início</Label>
              <Input type="time" value={startTime} onChange={(e) => handleStartTimeChange(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Término</Label>
              <Input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Cor</Label>
            <div className="flex flex-wrap gap-2">
              {EVENT_COLORS.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setColor(c.id)}
                  title={c.label}
                  className={`h-7 w-7 rounded-full transition-all ${
                    color === c.id
                      ? "ring-2 ring-offset-2 ring-foreground/60 scale-110"
                      : "hover:scale-105"
                  }`}
                  style={{ backgroundColor: c.hex }}
                />
              ))}
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          {isEdit && onDelete && initial?.id && (
            confirmDelete ? (
              <div className="flex w-full sm:w-auto gap-2 mr-auto">
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => {
                    onDelete(initial.id!);
                    onOpenChange(false);
                  }}
                >
                  Confirmar exclusão
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(false)}>
                  Cancelar
                </Button>
              </div>
            ) : (
              <Button variant="ghost" size="sm" className="mr-auto text-destructive hover:text-destructive" onClick={() => setConfirmDelete(true)}>
                <Trash2 className="h-4 w-4" /> Excluir
              </Button>
            )
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={!title.trim()}>
            {isEdit ? "Salvar" : "Criar evento"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}